// Who draws the butterfly. The WebGL wing planes draw it until the WebGPU monarch has
// drawn its first frame, then a handoff passes it over (or, with reduced motion, the monarch
// just appears). A failure, or the page leaving, falls back to the wing planes, and a later
// `start()` can try again. The portrait's `data-butterfly-renderer` names the mode, for CSS
// and tests.
//
//   webgl ──▶ first GPU frame ──┬── motion on ──▶ handoff ── the pass ends ──▶ webgpu
//     ▲                         └── reduced motion ─────────────────────────▶ webgpu
//     │
//     └── stop(): a lost device, a failed frame, or the page leaving (any mode)

import type { HalftoneData, WingStats } from "@bfly/engine";
import type { MonarchRenderer } from "./monarch-webgpu.js";
import type { ButterflyHandoffController } from "./butterfly-handoff.js";

type ButterflyMode = "webgl" | "handoff" | "webgpu";

interface HomeMonarchHost {
  onModeChange(mode: ButterflyMode): void;
  canAnimate(): boolean;
  onFlap(): void;
  /** The butterfly's renderer or mode changed, so draw a frame. */
  onChange(): void;
}

export class HomeMonarch {
  private renderer: MonarchRenderer | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private handoff: ButterflyHandoffController | null = null;
  private generation = 0;
  private mode: ButterflyMode = "webgl";
  private farShade = 1;

  constructor(
    private readonly portrait: HTMLElement,
    private readonly fallback: HTMLCanvasElement,
    private readonly before: Element,
    private readonly data: HalftoneData,
    private readonly host: HomeMonarchHost,
  ) {
    // Output only, for CSS and tests.
    this.portrait.dataset.butterflyRenderer = this.mode;
  }

  get active(): boolean {
    return this.renderer?.active ?? false;
  }

  /** The WebGL butterfly draws unless WebGPU has taken over completely. */
  get drawsFallback(): boolean {
    return this.mode !== "webgpu";
  }

  private setMode(mode: ButterflyMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.portrait.dataset.butterflyRenderer = mode;
    this.host.onModeChange(mode);
  }

  /** The far wing's colors, as a fraction of the palette's (`PortraitLook.farWing`). */
  setFarShade(shade: number): void {
    this.farShade = shade;
    this.renderer?.setFarShade(shade);
  }

  render(now: number, beat: WingStats, effectsOn: boolean): void {
    this.renderer?.render(now, beat, effectsOn);
  }

  resize(): void {
    this.renderer?.resize(this.size());
  }

  completeHandoff(): void {
    this.handoff?.complete();
  }

  private size(): number {
    // The layout width, not the client rect, because the portrait is rotated.
    return this.portrait.offsetWidth * 0.4 * Math.min(window.devicePixelRatio || 1, 2);
  }

  stop(): void {
    // A renderer that never started drew nothing, so the page hasn't changed. The host only
    // needs to react if a butterfly was showing.
    const shown = this.renderer !== null;
    // Invalidate any pending setup before disposing can report a device loss.
    this.generation++;
    this.handoff?.cancel();
    this.handoff = null;
    this.renderer?.dispose();
    this.renderer = null;
    this.canvas?.remove();
    this.canvas = null;
    this.setMode("webgl");
    if (shown) this.host.onChange();
  }

  async start(): Promise<void> {
    if (this.renderer) return;
    const generation = ++this.generation;
    try {
      if (!navigator.gpu) return;
      const [{ MonarchRenderer }, { startButterflyHandoff }] = await Promise.all([
        import("./monarch-webgpu.js"),
        import("./butterfly-handoff.js"),
      ]);
      if (generation !== this.generation) return;
      const gpuCanvas = document.createElement("canvas");
      gpuCanvas.className = "bfly-gpu";
      gpuCanvas.hidden = true;
      gpuCanvas.setAttribute("aria-hidden", "true");
      this.portrait.insertBefore(gpuCanvas, this.before);
      this.canvas = gpuCanvas;
      const renderer = await MonarchRenderer.create(gpuCanvas, this.size(), this.data, () => {
        if (generation === this.generation) this.stop();
      });
      // The page can leave while the adapter or pipelines are still pending.
      if (generation !== this.generation) {
        renderer?.dispose();
        gpuCanvas.remove();
        return;
      }
      if (!renderer) {
        this.stop();
        return;
      }
      this.renderer = renderer;
      renderer.setFarShade(this.farShade);
      gpuCanvas.style.opacity = "0";
      gpuCanvas.hidden = false;
      const animateHandoff = this.host.canAnimate();
      this.setMode(animateHandoff ? "handoff" : "webgpu");
      this.host.onChange();
      if (!animateHandoff) {
        gpuCanvas.style.removeProperty("opacity");
        return;
      }
      renderer.prepareCapture();
      await renderer.device.queue.onSubmittedWorkDone();
      if (generation !== this.generation || !renderer.active) return;
      // The motion setting can change while the GPU finishes its first frame.
      if (!this.host.canAnimate()) {
        this.setMode("webgpu");
        gpuCanvas.style.removeProperty("opacity");
        this.host.onChange();
        return;
      }
      this.handoff?.cancel();
      this.handoff = startButterflyHandoff({
        portrait: this.portrait,
        fallback: this.fallback,
        model: gpuCanvas,
        before: this.before,
        onFlap: () => this.host.onFlap(),
        onComplete: () => {
          if (generation !== this.generation || !renderer.active) return;
          this.setMode("webgpu");
          this.handoff = null;
          this.host.onChange();
        },
      });
      if (!this.handoff) {
        this.setMode("webgpu");
        gpuCanvas.style.removeProperty("opacity");
        this.host.onChange();
      }
    } catch (error) {
      if (generation === this.generation) this.stop();
      console.warn("WebGPU butterfly is unavailable; using dot butterfly", error);
    }
  }
}
